// DA-14969: единая декларативная схема вертикального порядка слоёв в Мартине.
//
// Шкала, сверху (виднее всего) вниз:
//   label   — подписи (текстовые symbol)
//   point   — точечные фичи (circle, symbol-иконки)
//   line    — линии
//   wash    — размывки над полигонами (heatmap, полупрозрачные заливки)
//   polygon — обычные полигоны (fill)
//   basemap — подложка (Mapbox base, hillshade) — не трогаем, всегда снизу
//
// Чем выше rank — тем выше слой на карте. basemap (background, hillshade,
// слои базовой карты Mapbox) сюда не входит и не двигается.
export const Z_RANK = {
    label: 50,
    point: 40,
    line: 30,
    wash: 20,
    polygon: 10,
};

// Каркас для будущих слоёв: zClass выводится из geometry type автоматически.
// Переопределить можно явным полем `zClass` в определении слоя (constants/layers.js),
// напр. symbol-иконке точечной фичи прописать zClass: 'point'.
export const resolveZClass = layer => {
    if (!layer) return null;

    switch (layer.type) {
        case 'circle':
            return 'point';
        case 'line':
            return 'line';
        case 'heatmap':
            return 'wash';
        case 'fill':
        case 'fill-extrusion':
            return 'polygon';
        case 'symbol': {
        // symbol неоднозначен: текстовая подпись vs точечная иконка.
            const layout = layer.layout || {};
            const hasText = 'text-field' in layout;
            const hasIcon = 'icon-image' in layout;
            if (hasIcon && !hasText) return 'point';
            return 'label';
        }
        default:
            return null; // background и прочее — не наш overlay
    }
};

// Слой относится к нашим overlay (его можно переупорядочивать), а не к подложке.
// Эвристика согласована с прежним moveWalkscoreBelowOverlays: базовая карта Mapbox
// и hillshade исключаются.
const isManagedOverlay = (map, layer) => {
    if (!layer || layer.id === 'hillshade') return false;
    if (![ 'circle', 'line', 'symbol', 'fill', 'fill-extrusion', 'heatmap' ].includes(layer.type)) {
        return false;
    }
    if (!layer.source) return false;
    const src = map.getSource(layer.source);
    if (!src) return false;
    const ref = src.url || (src.tiles && src.tiles[0]) || '';
    return !ref.startsWith('mapbox://');
};

// Пересортировать наши overlay-слои согласно Z_RANK, сохраняя относительный
// порядок внутри одной группы (стабильно) и не трогая подложку.
// zClassById — Map<layerId, zClass> с явными переопределениями из определений слоёв.
export const reorderOverlays = (map, zClassById = new Map()) => {
    if (!map || typeof map.getStyle !== 'function') return;
    let styleLayers;
    try {
        styleLayers = (map.getStyle().layers) || [];
    } catch (e) {
        return; // стиль ещё не готов
    }

    const rankOf = layer => {
        const explicit = zClassById.get(layer.id);
        const zClass = explicit || resolveZClass(layer);
        return Z_RANK[zClass] ?? null;
    };
    const managed = styleLayers
        .filter(layer => isManagedOverlay(map, layer))
        .map((layer, idx) => ({ id: layer.id, rank: rankOf(layer), idx }))
        .filter(item => item.rank !== null);

    // Стабильная сортировка по возрастанию rank (polygon … label); внутри группы — по исходному порядку.
    managed.sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx));

    // moveLayer без beforeId уводит слой наверх. Идём от низа к верху —
    // последний (label) окажется сверху, подложка не тронута.
    managed.forEach(({ id }) => {
        try {
            if (map.getLayer(id)) map.moveLayer(id);
        } catch (e) { /* слой мог исчезнуть между кадрами */ }
    });
};
